/* eslint-disable no-nested-ternary */
import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, ClickAwayListener, IconButton, SxProps, Typography } from '@mui/material';
import React, { ChangeEvent, MouseEvent, useRef, useState, useEffect } from 'react';
import { CustomMultiSelectOption } from './types';
import { List, CustomMultiSelectContainer } from './components';
import { IconDotsVertical } from '@tabler/icons';
import { useTheme } from '@mui/material/styles';

export type CustomMultiSelectProps = {
    options: CustomMultiSelectOption[];
    value: number[];
    onChange: (selectedOptions: number[]) => void;
    sx?: SxProps;
    ExpandIcon?: JSX.Element;
    ContractIcon?: JSX.Element;
    disabled?: boolean;
    error?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    loading?: boolean;
    label?: string;
};

export const CustomMultiSelect = ({
    options,
    value,
    onChange,
    sx,
    ExpandIcon,
    ContractIcon,
    disabled,
    error,
    onOpen,
    onClose,
    loading,
    label
}: CustomMultiSelectProps) => {
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchText, setSearchText] = useState('');
    const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
    const filteredOptions = options.filter((el) => el.label.toLowerCase().includes(searchText.toLowerCase()));
    const open = Boolean(anchorEl);
    const [allSelected, setAllSelected] = useState(false);
    const theme = useTheme();

    const handleOpenOptions = (event: MouseEvent<HTMLButtonElement>) => {
        onOpen?.();
        setAnchorEl(containerRef.current);
        setTimeout(() => {
            if (searchInputRef.current) searchInputRef.current.focus();
        }, 100);
    };

    const handleClose = () => {
        setAnchorEl(null);
        onClose?.();
    };

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        setSearchText(newVal);
    };

    const handleChangeSelectedOptions = (selectedIds: number[]) => () => {
        const newSelectedOptions = options.filter((el) => selectedIds.includes(el.id)).map((el) => el.id);
        setSelectedOptions(newSelectedOptions);
        onChange(newSelectedOptions);
    };

    const handleSelectAll = () => {
        const allOptionIds = options.map((option) => option.id);
        setSelectedOptions(allOptionIds);
        onChange(allOptionIds);
        setAllSelected(true);
    };

    const handleUnselectAll = () => {
        setSelectedOptions([]);
        onChange([]);
        setAllSelected(false);
    };

    useEffect(() => {
        if (options.length > 0) {
            const initialSelectedOptions = options.filter((el) => value.includes(el.id)).map((el) => el.id);
            setSelectedOptions(initialSelectedOptions);
            setAllSelected(initialSelectedOptions.length === options.length);
        } else {
            setSelectedOptions([]);
            setAllSelected(false);
        }
    }, [options, value]);

    const selectedOptionsLabel = React.useMemo(() => {
        const labels = options.filter((el) => selectedOptions.includes(el.id)).map((el) => el.label);
        const result = labels.join(', ');
        return result;
    }, [options, selectedOptions]);

    const backgroundColorHandler = () => {
        if (disabled) return theme.palette.primary[500];
        return open ? theme.palette.secondary.light : theme.palette.primary[300];
    };

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <CustomMultiSelectContainer
                ref={containerRef}
                open={open}
                sx={{
                    borderColor: error ? 'red' : open ? theme.palette.secondary.main : '#C6C4C4'
                }}
            >
                <Box
                    ref={containerRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        height: '100%',
                        width: '100%',
                        backgroundColor: backgroundColorHandler(),
                        position: 'relative',
                        ...sx
                    }}
                >
                    {label && (
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                top: '-15px',
                                left: '5px',
                                px: 0.5,
                                color: '#666',
                                backgroundColor: '#fff',
                                '& span': {
                                    color: '#C10230'
                                }
                            }}
                        >
                            <span>*</span>
                            {label}
                        </Typography>
                    )}
                    <Typography
                        fontSize="0.875rem"
                        title={selectedOptionsLabel}
                        sx={{
                            cursor: value ? 'pointer' : 'auto',
                            maxHeight: '100%',
                            maxWidth: '90%',
                            flexGrow: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 500,
                            color: '#000'
                        }}
                    >
                        {selectedOptionsLabel}
                    </Typography>

                    {!disabled && selectedOptions.length > 0 && (
                        <IconButton disableRipple onClick={handleChangeSelectedOptions([])} sx={{ ml: 'auto', pr: 0 }}>
                            <Close sx={{ fontSize: '1rem', color: theme.palette.primary[400] }} />
                        </IconButton>
                    )}

                    {disabled && (
                        <IconButton
                            sx={{ color: theme.palette.mode === 'dark' ? 'primary.main' : 'inherit', cursor: 'pointer' }}
                            onClick={handleOpenOptions}
                        >
                            <IconDotsVertical
                                stroke={1.5}
                                aria-controls={open ? 'menu-list-grow' : undefined}
                                aria-haspopup="true"
                                style={{ fontSize: '1.5rem' }}
                            />
                        </IconButton>
                    )}

                    {!disabled && (
                        <IconButton disableRipple onClick={handleOpenOptions} sx={{ ml: 'auto', px: 0 }}>
                            {open
                                ? ContractIcon ?? <ExpandLess sx={{ fontSize: '1rem', color: theme.palette.primary[400] }} />
                                : ExpandIcon ?? <ExpandMore sx={{ fontSize: '1rem', color: theme.palette.primary[400] }} />}
                        </IconButton>
                    )}

                    <List
                        open={open}
                        anchorEl={anchorEl}
                        searchInputRef={searchInputRef}
                        containerRef={containerRef}
                        searchText={searchText}
                        options={options}
                        filteredOptions={filteredOptions}
                        onClose={handleClose}
                        onChangeSearch={handleChangeSearch}
                        selectedOptions={selectedOptions}
                        onSelectOption={handleChangeSelectedOptions}
                        onClearSearch={() => setSearchText('')}
                        onSelectAll={handleSelectAll}
                        onUnselectAll={handleUnselectAll}
                        allSelected={allSelected}
                        disabled={disabled}
                        loading={loading}
                    />
                </Box>
            </CustomMultiSelectContainer>
        </ClickAwayListener>
    );
};
